import React, { createContext, useContext, useState } from "react";
import { toast } from "react-toastify";
import { useCommonFilterOptions } from "./CommonFilterOptionsContext";
import {
  fetchCallExecutiveMetadata,
  fetchCallExecutiveTodayMetadata,
  fetchCallExecutiveWrongNumberMetadata,
  fetchCallExecutiveInProgressMetadata,
  fetchCallManagerOrderInProgressMetadata,
  fetchCallManagerOrderEligibleMetadata,
  fetchCallManagerFurtherReviewMetadata,
  fetchCallExecutiveUnderAgeMetadata,
  fetchCallExecutiveInterviewMetadata,
  fetchCallExecutiveNotOnAiMetadata,
  fetchMyCallMetadata,
  fetchDeactivateCandidateMetadata,
} from "model/CallModel";

const CallExecutiveContext = createContext();

/**
 * Same pattern as JobCategoryContext: cache metadata via `fetchMetadataSchema`,
 * expose table + filters for Executive Schedule / Today pages.
 */
export const CallExecutiveProvider = ({ children }) => {
  const { fetchMetadataSchema } = useCommonFilterOptions();
  const [callExecutiveScheduleContextData, setCallExecutiveScheduleContextData] =
    useState();
  const [callExecutiveTodayContextData, setCallExecutiveTodayContextData] =
    useState();
  const [callExecutiveWrongNumberContextData, setCallExecutiveWrongNumberContextData] =
    useState();
  const [callExecutiveInProgressContextData, setCallExecutiveInProgressContextData] =
    useState();
  const [
    callManagerOrderInProgressContextData,
    setCallManagerOrderInProgressContextData,
  ] = useState();
  const [
    callManagerOrderEligibleContextData,
    setCallManagerOrderEligibleContextData,
  ] = useState();
  const [
    callManagerFurtherReviewContextData,
    setCallManagerFurtherReviewContextData,
  ] = useState();
  const [callExecutiveUnderAgeContextData, setCallExecutiveUnderAgeContextData] =
    useState();
  const [callExecutiveInterviewContextData, setCallExecutiveInterviewContextData] =
    useState();
  const [callExecutiveNotOnAiContextData, setCallExecutiveNotOnAiContextData] =
    useState();
  const [callExecutiveMyCallContextData, setCallExecutiveMyCallContextData] =
    useState();
  const [
    callExecutiveDeactivateCandidateContextData,
    setCallExecutiveDeactivateCandidateContextData,
  ] = useState();
  const [scheduleSchemaVersion, setScheduleSchemaVersion] = useState();
  const [todaySchemaVersion, setTodaySchemaVersion] = useState();
  const [wrongNumberSchemaVersion, setWrongNumberSchemaVersion] = useState();
  const [inProgressSchemaVersion, setInProgressSchemaVersion] = useState();
  const [
    callManagerOrderInProgressSchemaVersion,
    setCallManagerOrderInProgressSchemaVersion,
  ] = useState();
  const [
    callManagerOrderEligibleSchemaVersion,
    setCallManagerOrderEligibleSchemaVersion,
  ] = useState();
  const [
    callManagerFurtherReviewSchemaVersion,
    setCallManagerFurtherReviewSchemaVersion,
  ] = useState();
  const [underAgeSchemaVersion, setUnderAgeSchemaVersion] = useState();
  const [interviewSchemaVersion, setInterviewSchemaVersion] = useState();
  const [notOnAiSchemaVersion, setNotOnAiSchemaVersion] = useState();
  const [myCallSchemaVersion, setMyCallSchemaVersion] = useState();
  const [deactivateCandidateSchemaVersion, setDeactivateCandidateSchemaVersion] =
    useState();

  const fetchCallExecutiveScheduleContextData = async () => {
    try {
      const response = await fetchMetadataSchema(
        "metadata:callExecutiveScheduled",
        () => fetchCallExecutiveMetadata()
      );
      if (response?.success) {
        const data = response?.data ?? {};
        setCallExecutiveScheduleContextData(data);
        setScheduleSchemaVersion(response?.schemaVersion ?? data?.version);
        return data;
      }
      const data = response?.data ?? response;
      if (data && (data?.filters || data?.table)) {
        setCallExecutiveScheduleContextData(data);
        setScheduleSchemaVersion(response?.schemaVersion ?? data?.version);
        return data;
      }
    } catch (err) {
      toast.error(err?.message || err);
    }
    return null;
  };

  const fetchCallExecutiveTodayContextData = async () => {
    try {
      const response = await fetchMetadataSchema("metadata:callExecutiveToday", () =>
        fetchCallExecutiveTodayMetadata()
      );
      if (response?.success) {
        const data = response?.data ?? {};
        setCallExecutiveTodayContextData(data);
        setTodaySchemaVersion(response?.schemaVersion ?? data?.version);
        return data;
      }
      const data = response?.data ?? response;
      if (data && (data?.filters || data?.table)) {
        setCallExecutiveTodayContextData(data);
        setTodaySchemaVersion(response?.schemaVersion ?? data?.version);
        return data;
      }
    } catch (err) {
      toast.error(err?.message || err);
    }
    return null;
  };

  const fetchCallExecutiveWrongNumberContextData = async () => {
    try {
      const response = await fetchMetadataSchema(
        "metadata:callExecutiveWrongNumber",
        () => fetchCallExecutiveWrongNumberMetadata()
      );
      if (response?.success) {
        const data = response?.data ?? {};
        setCallExecutiveWrongNumberContextData(data);
        setWrongNumberSchemaVersion(response?.schemaVersion ?? data?.version);
        return data;
      }
      const data = response?.data ?? response;
      if (data && (data?.filters || data?.table)) {
        setCallExecutiveWrongNumberContextData(data);
        setWrongNumberSchemaVersion(response?.schemaVersion ?? data?.version);
        return data;
      }
    } catch (err) {
      toast.error(err?.message || err);
    }
    return null;
  };

  const fetchCallExecutiveInProgressContextData = async () => {
    try {
      const response = await fetchMetadataSchema(
        "metadata:callExecutiveInProgress",
        () => fetchCallExecutiveInProgressMetadata()
      );
      if (response?.success) {
        const data = response?.data ?? {};
        setCallExecutiveInProgressContextData(data);
        setInProgressSchemaVersion(response?.schemaVersion ?? data?.version);
        return data;
      }
      const data = response?.data ?? response;
      if (data && (data?.filters || data?.table)) {
        setCallExecutiveInProgressContextData(data);
        setInProgressSchemaVersion(response?.schemaVersion ?? data?.version);
        return data;
      }
    } catch (err) {
      toast.error(err?.message || err);
    }
    return null;
  };

  const fetchCallManagerOrderInProgressContextData = async () => {
    try {
      const response = await fetchMetadataSchema(
        "metadata:callManagerOrderInProgress",
        () => fetchCallManagerOrderInProgressMetadata()
      );
      if (response?.success) {
        const data = response?.data ?? {};
        setCallManagerOrderInProgressContextData(data);
        setCallManagerOrderInProgressSchemaVersion(
          response?.schemaVersion ?? data?.version
        );
        return data;
      }
      const data = response?.data ?? response;
      if (data && (data?.filters || data?.table)) {
        setCallManagerOrderInProgressContextData(data);
        setCallManagerOrderInProgressSchemaVersion(
          response?.schemaVersion ?? data?.version
        );
        return data;
      }
    } catch (err) {
      toast.error(err?.message || err);
    }
    return null;
  };

  const fetchCallManagerOrderEligibleContextData = async () => {
    try {
      const response = await fetchMetadataSchema(
        "metadata:callManagerOrderEligible",
        () => fetchCallManagerOrderEligibleMetadata()
      );
      if (response?.success) {
        const data = response?.data ?? {};
        setCallManagerOrderEligibleContextData(data);
        setCallManagerOrderEligibleSchemaVersion(
          response?.schemaVersion ?? data?.version
        );
        return data;
      }
      const data = response?.data ?? response;
      if (data && (data?.filters || data?.table)) {
        setCallManagerOrderEligibleContextData(data);
        setCallManagerOrderEligibleSchemaVersion(
          response?.schemaVersion ?? data?.version
        );
        return data;
      }
    } catch (err) {
      toast.error(err?.message || err);
    }
    return null;
  };

  const fetchCallManagerFurtherReviewContextData = async () => {
    try {
      const response = await fetchMetadataSchema(
        "metadata:callManagerFurtherReview",
        () => fetchCallManagerFurtherReviewMetadata()
      );
      if (response?.success) {
        const data = response?.data ?? {};
        setCallManagerFurtherReviewContextData(data);
        setCallManagerFurtherReviewSchemaVersion(
          response?.schemaVersion ?? data?.version
        );
        return data;
      }
      const data = response?.data ?? response;
      if (data && (data?.filters || data?.table)) {
        setCallManagerFurtherReviewContextData(data);
        setCallManagerFurtherReviewSchemaVersion(
          response?.schemaVersion ?? data?.version
        );
        return data;
      }
    } catch (err) {
      toast.error(err?.message || err);
    }
    return null;
  };

  const fetchCallExecutiveUnderAgeContextData = async () => {
    try {
      const response = await fetchMetadataSchema(
        "metadata:callExecutiveUnderAge",
        () => fetchCallExecutiveUnderAgeMetadata()
      );
      if (response?.success) {
        const data = response?.data ?? {};
        setCallExecutiveUnderAgeContextData(data);
        setUnderAgeSchemaVersion(response?.schemaVersion ?? data?.version);
        return data;
      }
      const data = response?.data ?? response;
      if (data && (data?.filters || data?.table)) {
        setCallExecutiveUnderAgeContextData(data);
        setUnderAgeSchemaVersion(response?.schemaVersion ?? data?.version);
        return data;
      }
    } catch (err) {
      toast.error(err?.message || err);
    }
    return null;
  };

  const fetchCallExecutiveInterviewContextData = async () => {
    try {
      const response = await fetchMetadataSchema(
        "metadata:callExecutiveInterview",
        () => fetchCallExecutiveInterviewMetadata()
      );
      if (response?.success) {
        const data = response?.data ?? {};
        setCallExecutiveInterviewContextData(data);
        setInterviewSchemaVersion(response?.schemaVersion ?? data?.version);
        return data;
      }
      const data = response?.data ?? response;
      if (data && (data?.filters || data?.table)) {
        setCallExecutiveInterviewContextData(data);
        setInterviewSchemaVersion(response?.schemaVersion ?? data?.version);
        return data;
      }
    } catch (err) {
      toast.error(err?.message || err);
    }
    return null;
  };

  const fetchCallExecutiveNotOnAiContextData = async () => {
    try {
      const response = await fetchMetadataSchema(
        "metadata:callExecutiveNotOnAi",
        () => fetchCallExecutiveNotOnAiMetadata()
      );
      if (response?.success) {
        const data = response?.data ?? {};
        setCallExecutiveNotOnAiContextData(data);
        setNotOnAiSchemaVersion(response?.schemaVersion ?? data?.version);
        return data;
      }
      const data = response?.data ?? response;
      if (data && (data?.filters || data?.table)) {
        setCallExecutiveNotOnAiContextData(data);
        setNotOnAiSchemaVersion(response?.schemaVersion ?? data?.version);
        return data;
      }
    } catch (err) {
      toast.error(err?.message || err);
    }
    return null;
  };

  const fetchCallExecutiveMyCallContextData = async () => {
    try {
      const response = await fetchMetadataSchema("metadata:callExecutiveMyCall", () =>
        fetchMyCallMetadata()
      );
      if (response?.success) {
        const data = response?.data ?? {};
        setCallExecutiveMyCallContextData(data);
        setMyCallSchemaVersion(response?.schemaVersion ?? data?.version);
        return data;
      }
      const data = response?.data ?? response;
      if (data && (data?.filters || data?.table)) {
        setCallExecutiveMyCallContextData(data);
        setMyCallSchemaVersion(response?.schemaVersion ?? data?.version);
        return data;
      }
    } catch (err) {
      toast.error(err?.message || err);
    }
    return null;
  };

  const fetchCallExecutiveDeactivateCandidateContextData = async () => {
    try {
      const response = await fetchMetadataSchema(
        "metadata:callExecutiveDeactivateCandidate",
        () => fetchDeactivateCandidateMetadata()
      );
      if (response?.success) {
        const data = response?.data ?? {};
        setCallExecutiveDeactivateCandidateContextData(data);
        setDeactivateCandidateSchemaVersion(
          response?.schemaVersion ?? data?.version
        );
        return data;
      }
      const data = response?.data ?? response;
      if (data && (data?.filters || data?.table)) {
        setCallExecutiveDeactivateCandidateContextData(data);
        setDeactivateCandidateSchemaVersion(
          response?.schemaVersion ?? data?.version
        );
        return data;
      }
    } catch (err) {
      toast.error(err?.message || err);
    }
    return null;
  };

  return (
    <CallExecutiveContext.Provider
      value={{
        fetchCallExecutiveScheduleContextData,
        fetchCallExecutiveTodayContextData,
        fetchCallExecutiveWrongNumberContextData,
        fetchCallExecutiveInProgressContextData,
        fetchCallManagerOrderInProgressContextData,
        fetchCallManagerOrderEligibleContextData,
        fetchCallManagerFurtherReviewContextData,
        fetchCallExecutiveUnderAgeContextData,
        fetchCallExecutiveInterviewContextData,
        fetchCallExecutiveNotOnAiContextData,
        fetchCallExecutiveMyCallContextData,
        fetchCallExecutiveDeactivateCandidateContextData,
        callExecutiveScheduleContextData,
        callExecutiveTodayContextData,
        callExecutiveWrongNumberContextData,
        callExecutiveInProgressContextData,
        callManagerOrderInProgressContextData,
        callManagerOrderEligibleContextData,
        callManagerFurtherReviewContextData,
        callExecutiveUnderAgeContextData,
        callExecutiveInterviewContextData,
        callExecutiveNotOnAiContextData,
        callExecutiveMyCallContextData,
        callExecutiveDeactivateCandidateContextData,
        scheduleSchemaVersion,
        todaySchemaVersion,
        wrongNumberSchemaVersion,
        inProgressSchemaVersion,
        callManagerOrderInProgressSchemaVersion,
        callManagerOrderEligibleSchemaVersion,
        callManagerFurtherReviewSchemaVersion,
        underAgeSchemaVersion,
        interviewSchemaVersion,
        notOnAiSchemaVersion,
        myCallSchemaVersion,
        deactivateCandidateSchemaVersion,
      }}
    >
      {children}
    </CallExecutiveContext.Provider>
  );
};

export const useCallExecutiveContext = () =>
  useContext(CallExecutiveContext) ?? {};
